<template>
  <validated-form
    form-id="prepTimeRulesForm"
    @valid-submit="handleSubmit"
  >
    <panel
      :title="storeId ? `${storeName || 'Location'} Prep Time Rules` : 'Merchant Prep Time Rules'"
      :subtitle="storeId ? 'Configure store-specific rules to automatically adjust prep times based on order details.' : 'Configure rules to automatically adjust prep times based on order details.'"
      :loading="loading"
      no-padding
    >
      <template #buttons>
        <b-button
          v-if="changed"
          rounded
          size="is-medium"
          class="is-bold has-text-white"
          type="is-warning"
          @click="revertChanges"
        >
          Revert Changes
        </b-button>

        <b-button
          rounded
          size="is-medium"
          class="is-bold"
          :class="{ 'pulse-slow': changed }"
          type="is-primary"
          :loading="updating"
          :disabled="!changed"
          native-type="submit"
        >
          Save
        </b-button>
      </template>

      <hr class="mar-t-none">

      <div class="pad-b pad-x">
        <div class="dist-y-md is-flex-column align-center">
          <draggable
            v-model="orderRules"
            tag="div"
            class="dist-y-md"
            v-bind="draggableAttributes"
            @change="handleOrderRuleSort"
          >
            <div
              v-for="(orderRule, index) in orderRules"
              :key="`order-rule-${index}`"
              class="card draggable has-background-white-ter"
            >
              <div class="card-content">
                <div class="order-rule-row is-grid gap">
                  <div class="is-flex align-center space-between gap">
                    <b-icon icon="grip-lines" size="is-medium" pack="far" class="drag-handle is-full-height" />
                    <div class="is-flex-column align-center justify-center gap">
                      <h3 class="title is-5 mar-none">Rule {{ index + 1 }}</h3>
                      <b-switch
                        v-model="orderRule.isActive"
                        class="left-label"
                      />
                    </div>
                  </div>
                  <validated-text-input
                    v-model="orderRules[index].minutesToAdd"
                    type="number"
                    name="minutesToAdd"
                    label="Minutes to Add"
                    label-position="on-border"
                    placeholder="Enter minutes"
                    rules="required|numeric|min_value:0.01"
                    class="align-self-center"
                  />
                  <div class="segments-container is-flex-column">
                    <div
                      v-for="(segment, segmentIndex) in orderRule.ruleSegments"
                      :key="`segment-${orderRule.id || index}-${segmentIndex}`"
                      class="segment-row is-flex gap-xs pad-x-sm pad-y-xs"
                    >
                      <div class="is-grid col-3 gap-xs">
                        <validated-input
                          class="flex-grow mar-b-none"
                          :name="`memberName-${orderRule.id || index}-${segmentIndex}`"
                          label="Type"
                          label-position="on-border"
                          :hide-label="segmentIndex > 0"
                          rules="required"
                        >
                          <b-select
                            :value="orderRule.ruleSegments[segmentIndex].memberName"
                            placeholder="Select type..."
                            expanded
                            @input="handleMemberNameChange($event, orderRule, index, segmentIndex)"
                          >
                            <option :value="null" disabled>-</option>
                            <option value="Category">Category</option>
                            <option value="OrderItemTotal">Order Item Total</option>
                          </b-select>
                        </validated-input>

                        <validated-input
                          class="flex-grow mar-b-none"
                          :name="`action-${orderRule.id || index}-${segmentIndex}`"
                          label="Condition"
                          label-position="on-border"
                          :hide-label="segmentIndex > 0"
                          rules="required"
                        >
                          <b-select
                            v-model="orderRule.ruleSegments[segmentIndex].action"
                            placeholder="Select condition..."
                            expanded
                            :disabled="orderRule.ruleSegments[segmentIndex].memberName === 'Category'"
                          >
                            <option :value="null" disabled>-</option>
                            <option
                              v-for="operator in equalityOperators"
                              :key="operator"
                              :value="operator"
                            >
                              {{ capitalCase(operator) }}
                            </option>
                          </b-select>
                        </validated-input>

                        <validated-input
                          v-if="orderRule.ruleSegments[segmentIndex].memberName === 'Category'"
                          class="flex-grow mar-b-none"
                          :name="`targetValue-${orderRule.id || index}-${segmentIndex}`"
                          label="Category"
                          label-position="on-border"
                          :hide-label="segmentIndex > 0"
                          rules="required"
                        >
                          <b-select
                            v-model="orderRule.ruleSegments[segmentIndex].targetValue"
                            placeholder="Select category..."
                            expanded
                          >
                            <option :value="null" disabled>-</option>
                            <option
                              v-for="category in categories"
                              :key="category"
                              :value="category"
                            >
                              {{ category }}
                            </option>
                          </b-select>
                        </validated-input>

                        <validated-text-input
                          v-else
                          v-model="orderRule.ruleSegments[segmentIndex].targetValue"
                          type="float"
                          :name="`targetValue-${orderRule.id || index}-${segmentIndex}`"
                          label="Value"
                          label-position="on-border"
                          :hide-label="segmentIndex > 0"
                          rules="required|numeric|min_value:0.01"
                          placeholder="Enter value"
                          class="flex-grow mar-b-none"
                        />
                      </div>
                      <b-button
                        v-if="orderRule.ruleSegments.length > 1"
                        type="is-transparent is-primary"
                        inverted
                        icon-left="times-circle"
                        @click="removeSegment(orderRule, segmentIndex)"
                      />
                    </div>

                    <b-button
                      type="is-primary is-light mar-l-auto mar-t-sm mar-r-sm"
                      icon-left="plus"
                      @click="addSegment(orderRule)"
                    >
                      Condition
                    </b-button>
                  </div>
                  <div class="is-flex align-center gap">
                    <b-button
                      outlined
                      icon-left="trash-alt"
                      type="is-danger is-light"
                      @click="removeOrderRule(index)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </draggable>

          <b-message
            v-if="orderRules.length === 0"
            size="is-meduim"
            class="is-compact has-shadow"
            type="is-info"
          >
            No prep time rules found for this {{ storeId ? 'store' : 'merchant' }}. Click <b>+ Rule</b> to create one.
          </b-message>

          <div class="align-self-end">
            <b-button
              type="is-danger"
              icon-left="times"
              class="mar-r-sm"
              @click="$emit('update', [])"
            >
              Clear All
            </b-button>

            <b-button
              type="is-primary"
              icon-left="plus"
              @click="addOrderRule"
            >
              Rule
            </b-button>
          </div>
        </div>
      </div>
    </panel>
  </validated-form>
</template>

<script>
  import { capitalCase } from 'change-case';
  import draggable from 'vuedraggable';

  import merchantMixin from '@/mixins/merchant';

  import OrderRule from '@/store/classes/OrderRule';
  import Store from '@/store/classes/Store';
  import getChangedResources from '@/helpers/get-changed-resources';


  export default {
    name: 'PrepTimeRulesForm',

    components: {
      draggable
    },

    mixins: [merchantMixin],

    props: {
      merchantId: {
        type: Number,
        required: true
      },
      storeId: {
        type: [Number, String],
        default: null
      },
      value: {
        type: Array,
        required: true
      }
    },

    data() {
      return {
        isLoading: false,
        categories: {
          RESTAURANT: 'Restaurant',
          CATERING: 'Catering'
        },
        equalityOperators: {
          LESS_THAN: 'LessThan',
          LESS_THAN_OR_EQUAL: 'LessThanOrEqual',
          EQUAL: 'Equal',
          GREATER_THAN: 'GreaterThan',
          GREATER_THAN_OR_EQUAL: 'GreaterThanOrEqual'
        },
        draggableAttributes: {
          animation: 200,
          ghostClass: 'ghost',
          handle: '.drag-handle',
          draggable: '.draggable'
        }
      };
    },

    computed: {
      orderRules: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        }
      },

      loading() {
        return OrderRule.$state().fetching;
      },

      updating() {
        return OrderRule.$state().updating;
      },

      changed() {
        return JSON.stringify(this.originalOrderRules) !== JSON.stringify(this.orderRules);
      },

      storeName() {
        return this.storeId ? Store.find(this.storeId)?.description : '';
      },

      originalOrderRules() {
        if (this.storeId) {
          return OrderRule
            .query()
            .where('merchantId', this.$_selectedMerchantId)
            .where('storeId', Number(this.storeId))
            .orderBy('priority')
            .get();
        }
        else {
          return OrderRule
            .query()
            .where('merchantId', this.$_selectedMerchantId)
            .where('storeId', null)
            .orderBy('priority')
            .get();
        }
      }
    },

    methods: {
      capitalCase,

      revertChanges() {
        this.$emit('update', this.$clone(this.originalOrderRules));
      },

      addOrderRule() {
        const newOrderRule = {
          isActive: true,
          merchantId: this.$_selectedMerchantId,
          storeId: this.storeId,
          minutesToAdd: null,
          priority: this.orderRules.length > 0 ? Math.max(...this.orderRules.map(rule => rule.priority)) + 1 : 1,
          ruleSegments: [
            { memberName: null, action: null, targetValue: null }
          ]
        };
        const updatedRules = [...this.orderRules, newOrderRule];
        this.$emit('update', updatedRules);
      },

      removeOrderRule(index) {
        const updatedRules = [...this.orderRules];
        updatedRules.splice(index, 1);
        const prioritizedRules = this.updateRulePriorities(updatedRules);
        this.$emit('update', prioritizedRules);
      },

      addSegment(orderRule) {
        orderRule.ruleSegments.push({
          memberName: null,
          action: null,
          targetValue: null
        });
        this.$emit('update', this.orderRules);
      },

      removeSegment(orderRule, segmentIndex) {
        orderRule.ruleSegments.splice(segmentIndex, 1);
        this.$emit('update', this.orderRules);
      },

      handleMemberNameChange(value, orderRule, orderRuleIndex, segmentIndex) {
        orderRule.ruleSegments[segmentIndex].memberName = value;
        orderRule.ruleSegments[segmentIndex].targetValue = null;
        orderRule.ruleSegments[segmentIndex].action = value === 'Category'
          ? this.equalityOperators.EQUAL
          : null;

        const orderRules = this.orderRules.map((existingOrderRule, index) => {
          if (index === orderRuleIndex) {
            return orderRule;
          }
          else {
            return existingOrderRule;
          }
        });

        this.$emit('update', orderRules);
      },

      handleOrderRuleSort({ moved }) {
        if (moved) {
          const reOrderedRules = this.reSortRules({
            fromIndex: moved.oldIndex,
            toIndex: moved.newIndex,
            array: [...this.orderRules]
          });
          this.$emit('update', reOrderedRules);
        }
      },

      reSortRules({ fromIndex, toIndex, array }) {
        // Code borrowed from `set-new-sort-orders.js` and modified to work for priority instead of sort order
        // make a deep clone of the array
        const rulesCopy = JSON.parse(JSON.stringify(array));

        // make sure the array of resources are sorted by their sortOrder value to start
        rulesCopy.sort((a, b) => ((a.priority < b.priority) ? -1 : 1));

        const fromPriority = rulesCopy[fromIndex].priority;
        const toPriority = rulesCopy[toIndex].priority;

        const _fromIndex = rulesCopy.findIndex(resource => resource.priority === fromPriority);
        const _toIndex = rulesCopy.findIndex(resource => resource.priority === toPriority);

        const [movedRule] = rulesCopy.splice(_fromIndex, 1);
        rulesCopy.splice(_toIndex, 0, movedRule);

        return this.updateRulePriorities(rulesCopy);
      },

      updateRulePriorities(orderRules) {
        return orderRules.map((rule, i) => {
          rule.priority = i + 1;
          return rule;
        });
      },

      async handleSubmit() {
        try {
          const { added, updated, removed } = getChangedResources({
            oldArray: this.originalOrderRules || [],
            newArray: this.orderRules,
            comparisonKey: 'id',
            updatedComparisonFn: (n, o) => JSON.stringify(n) !== JSON.stringify(o)
          });

          await Promise.all([
            ...added.map(a => OrderRule.createOrderRule(a)),
            ...updated.map(u => OrderRule.updateOrderRule(u)),
            ...removed.map(r => OrderRule.deleteOrderRule(r.id))
          ]);

          // The originalOrderRules pull directly from the VuexORM query. After successfully performing
          // CRUD on the all the VuexORM records for these rules have been updated accordingly. We need to
          // emit up these updated records to reset the form for the correct changed state in this component.
          this.$emit('update', this.$clone(this.originalOrderRules));

          this.$_onRequestSuccess({
            toastOptions: { message: 'Prep time rules saved successfully!' }
          });
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: { message: 'There was an error saving prep time rules' },
            error
          });
        }
      }
    }
  };
</script>

<style lang="sass" scoped>
.draggable
  cursor: move

.drag-handle
  cursor: grab

.ghost
  opacity: 0.5
  background: #c8ebfb

.order-rule-row
  grid-template-columns: 110px 125px 1fr 50px

.segments-container
  border-right: 1px solid $grey-lighter
  border-left: 1px solid $grey-lighter

.segment-row:nth-child(even)
  background-color: $grey-lightest !important
</style>
